export class APIClient {
  constructor(mobileUuid, eventBus) {
    this.mobileUuid = mobileUuid;
    this.eventBus = eventBus;

    this.username = null;
    this.accessToken = null;
    this.baseHeaders = {};
    this.requestTimeout = 10_000;
    this.renewalPromise = null;

    this.setupHeaders();
  }

  updateMobileUuid(uuid) {
    this.mobileUuid = uuid;
    this.setupHeaders();
    this.eventBus.dispatchEvent(
      new CustomEvent('api:mobileUuidUpdated', { detail: { uuid } })
    );
  }

  setupHeaders() {
    this.baseHeaders = {
      'X-Mobile-Uuid': this.mobileUuid,
      'Content-Type': 'application/json',
      Authorization: this.accessToken ? `Bearer ${this.accessToken}` : '',
    };
  }

  // Main fetch method with authentication
  async fetch(url, options = {}) {
    const request = async () => {
      if (options.signal?.aborted) {
        throw (
          options.signal.reason ??
          new DOMException('Request aborted', 'AbortError')
        );
      }

      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), this.requestTimeout);
      const abortRequest = () => controller.abort();
      options.signal?.addEventListener('abort', abortRequest, { once: true });

      try {
        return await fetch(url, {
          ...options,
          headers: {
            ...this.baseHeaders,
            ...options.headers,
          },
          credentials: 'same-origin',
          signal: controller.signal,
        });
      } finally {
        clearTimeout(timeout);
        options.signal?.removeEventListener('abort', abortRequest);
      }
    };

    let response = await request();

    const regex = /^api\/(notifications).*/;
    if (regex.test(url) && response.status === 401) {
      const renewResponse = await this.renewAccessToken();
      if (renewResponse.ok) {
        // Retry the original request with the new token
        response = await request();
      }
    }

    return response;
  }

  // Convenience methods for different HTTP verbs
  async get(url, options = {}) {
    return this.fetch(url, {
      method: 'GET',
      ...options,
    });
  }

  async post(url, data, options = {}) {
    return this.fetch(url, {
      method: 'POST',
      body: JSON.stringify(data),
      ...options,
    });
  }

  async patch(url, data, options = {}) {
    return this.fetch(url, {
      method: 'PATCH',
      body: JSON.stringify(data),
      ...options,
    });
  }

  async put(url, data, options = {}) {
    return this.fetch(url, {
      method: 'PUT',
      body: JSON.stringify(data),
      ...options,
    });
  }

  async delete(url, options = {}) {
    return this.fetch(url, {
      method: 'DELETE',
      ...options,
    });
  }

  getVapidPublicKey() {
    return this.get('api/vapidpublickey');
  }

  // Auth
  async register(username, password) {
    const response = await this.post('api/register', { username, password });
    if (response.ok) {
      const data = await response.json();
      this.accessToken = data.access_token;
      this.setupHeaders();
    }
    return response;
  }

  async login(username, password) {
    const response = await this.post('api/login', { username, password });
    if (response.ok) {
      const data = await response.json();
      this.username = data.username;
      this.accessToken = data.access_token;
      this.setupHeaders();
    }
    return response;
  }

  logout() {
    this.accessToken = null;
    this.setupHeaders();
    return this.get('api/logout');
  }

  async renewAccessToken() {
    if (this.renewalPromise) {
      return this.renewalPromise;
    }

    const renewal = (async () => {
      const response = await this.get('api/renew');
      if (response.ok) {
        const data = await response.json();
        this.username = data.username;
        this.accessToken = data.access_token;
        this.setupHeaders();
        this.eventBus.dispatchEvent(
          new CustomEvent('api:accessTokenRenewed', {
            detail: { username: this.username },
          })
        );
      } else if (response.status !== 429) {
        this.logout();
        this.eventBus.dispatchEvent(
          new CustomEvent('api:accessTokenRenewalFailed')
        );
      }
      return response;
    })();
    this.renewalPromise = renewal;

    try {
      return await renewal;
    } finally {
      if (this.renewalPromise === renewal) {
        this.renewalPromise = null;
      }
    }
  }

  // Mobile
  getMobile() {
    return this.get('api/mobiles');
  }

  updateMobile(subscription) {
    return this.patch('api/mobiles', {
      endpoint: subscription.endpoint,
      ...subscription.keys,
    });
  }

  registerMobile(uuid, subscription) {
    return this.post('api/mobiles', {
      uuid,
      endpoint: subscription.endpoint,
      ...subscription.keys,
    });
  }

  // Notification
  getNotifications() {
    return this.get('api/notifications');
  }

  createNotification(name, description) {
    return this.post('api/notifications', { name, description });
  }

  updateNotification(id, updates) {
    return this.patch(`api/notifications/${id}`, updates);
  }

  deleteNotification(id) {
    return this.delete(`api/notifications/${id}`);
  }

  // Subscription
  getSubscriptions() {
    return this.get('api/mobiles/subscriptions');
  }

  createSubscription(token) {
    return this.post(`api/mobiles/subscriptions`, { token });
  }

  updateSubscription(notificationId, updates) {
    return this.patch(`api/mobiles/subscriptions/${notificationId}`, updates);
  }

  deleteSubscription(notificationId) {
    return this.delete(`api/mobiles/subscriptions/${notificationId}`);
  }

  getMobileNotificationHistories(limit = 10, cursor = '') {
    const params = new URLSearchParams({ limit: String(limit) });
    if (cursor) {
      params.set('cursor', cursor);
    }
    return this.get(`api/mobiles/notifications/history?${params.toString()}`);
  }

  getMobileNotificationHistory(targetID) {
    return this.get(
      `api/mobiles/notifications/history/${encodeURIComponent(String(targetID))}`
    );
  }

  testNotification(notificationToken, body) {
    return this.post('api/notify', {
      title: 'Test Notification',
      body: body,
      token: notificationToken,
    });
  }

  autoLogin() {
    return this.renewAccessToken();
  }

  getNotificationLogs(notificationId) {
    return this.get(`api/notifications/${notificationId}/logs`);
  }
}
